import { useEffect, useRef, useState } from 'react';
import './Journey.css';

const chapters = [
  { at: 0, title: 'An arrival, unhurried.', label: '01 / The approach' },
  { at: .14, title: 'Drawn towards the light.', label: '02 / The approach' },
  { at: .25, title: 'A welcome in warm stone.', label: '03 / The entrance' },
  { at: .34, title: 'Outside becomes inside.', label: '04 / The threshold' },
  { at: .43, title: 'A line through the house.', label: '05 / The foyer' },
  { at: .53, title: 'Room to simply be.', label: '06 / The living room' },
  { at: .64, title: 'For the everyday rituals.', label: '07 / Kitchen & dining' },
  { at: .77, title: 'The beauty is in the detail.', label: '08 / Stone & light' },
  { at: .89, title: 'A quieter world awaits.', label: '09 / The water garden' },
];
const stills = [
  ['exterior','The approach','A travertine path beneath the olive trees.'],
  ['entrance','The threshold','An open oak door, a glimpse of the horizon.'],
  ['foyer','The foyer','A continuous line from landscape to living space.'],
  ['living','The living room','Low linen seating, long views, afternoon light.'],
  ['kitchen','Kitchen & dining','Oak and stone, composed for daily rituals.'],
  ['detail','Stone & light','The same material, from the outside in.'],
  ['terrace','The water garden','Still water beside the arrival path.'],
];

export default function Journey() {
  const root = useRef(null);
  const canvas = useRef(null);
  const title = useRef(null);
  const label = useRef(null);
  const progressBar = useRef(null);
  const progressInput = useRef(null);
  const hero = useRef(null);
  const [cinematic, setCinematic] = useState(false);
  const [ready, setReady] = useState(false);
  const [failure, setFailure] = useState(false);
  const [index, setIndex] = useState(0);
  const trigger = useRef(null);
  const entryHash = useRef(undefined);
  const scrollGallery = useRef(null);

  useEffect(() => {
    if(entryHash.current === undefined) entryHash.current = window.location.hash;
    const desktop = matchMedia('(min-width: 1024px)');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const choose = () => setCinematic(desktop.matches && !reduced.matches && (!navigator.deviceMemory || navigator.deviceMemory > 2) && !navigator.connection?.saveData && !/2g/.test(navigator.connection?.effectiveType || ''));
    choose();
    desktop.addEventListener('change',choose); reduced.addEventListener('change',choose);
    return () => { desktop.removeEventListener('change',choose); reduced.removeEventListener('change',choose); };
  }, []);

  useEffect(() => {
    if (!cinematic) return;
    let disposed = false, manifest, target = 0, lastDrawn = -1, active = 0, raf = 0, filmVisible = false;
    const cache = new Map(), pending = new Set(), failed = new Set(), controllers = new Set();
    const context = canvas.current.getContext('2d', { alpha: false });
    const stage = root.current;
    let currentProgress = 0;
    const setCaption = p => {
      if (disposed || !label.current || !title.current || !hero.current || !progressBar.current || !progressInput.current) return;
      const chapter = [...chapters].reverse().find(c=>p >= c.at) || chapters[0];
      label.current.textContent = chapter.label;
      title.current.textContent = chapter.title;
      progressBar.current.style.transform = `scaleX(${p})`;
      progressInput.current.value = Math.round(p*1000);
      progressInput.current.setAttribute('aria-valuetext', chapter.label.split('/ ')[1]);
      stage.dataset.progress = p.toFixed(3);
      hero.current.style.opacity = Math.max(0,1-p/.09);
      hero.current.style.transform = `translateY(${-Math.min(p/.09,1)*32}px)`;
      hero.current.style.visibility = p>.1?'hidden':'visible';
      stage.querySelector('.scene-caption').style.opacity = p>.08 ? 1 : 0;
      stage.querySelectorAll('.cinematic-still').forEach((el, n)=> {
        const starts=[.62,.76,.88];
        const a=Math.max(0, Math.min(1,(p-starts[n])/.035));
        el.style.opacity=a;
        el.style.transform=`scale(${1.035-Math.max(0,Math.min(1,(p-starts[n])/.12))*.035})`;
      });
    };
    const draw = () => {
      if (disposed || !cache.size) return;
      canvas.current.style.opacity = target > 0 ? 1 : 0;
      const nearest = [...cache.keys()].reduce((a,b)=>Math.abs(b-target)<Math.abs(a-target)?b:a);
      const bitmap = cache.get(nearest);
      if (lastDrawn===nearest && canvas.current.width) return;
      const width = Math.min(1920,Math.round(stage.clientWidth* Math.min(devicePixelRatio,1.25)));
      const height = Math.round(width*stage.clientHeight/stage.clientWidth);
      if(canvas.current.width!==width||canvas.current.height!==height){canvas.current.width=width;canvas.current.height=height;}
      const scale=Math.max(width/bitmap.width,height/bitmap.height);
      context.drawImage(bitmap,(width-bitmap.width*scale)/2,(height-bitmap.height*scale)/2,bitmap.width*scale,bitmap.height*scale);
      lastDrawn=nearest;
      canvas.current.dataset.frame = nearest;
      if(!filmVisible){filmVisible=true;}
    };
    const evict = () => {
      while(cache.size > 18){
        const farthest=[...cache.keys()].sort((a,b)=>Math.abs(b-target)-Math.abs(a-target))[0];
        cache.get(farthest).close(); cache.delete(farthest);
      }
    };
    const pump = () => {
      if(disposed || !manifest || currentProgress>.67) return;
      const wanted=[target];
      for(let d=1;d<=7;d++)wanted.push(target+d,target-d);
      for(const frame of wanted){
        if(active>=3)break;
        if(frame<0||frame>=manifest.count||cache.has(frame)||pending.has(frame)||failed.has(frame))continue;
        pending.add(frame);active++;
        const controller=new AbortController();controllers.add(controller);
        fetch(`/media/frames/${String(frame).padStart(4,'0')}.webp`,{signal:controller.signal})
          .then(r=>{if(!r.ok)throw Error('Frame unavailable');return r.blob();})
          .then(createImageBitmap)
          .then(bitmap=>{
            if(disposed){bitmap.close();return;}
            cache.set(frame,bitmap);evict();draw();setReady(true);
          }).catch(e=>{if(!disposed && e.name!=='AbortError'){failed.add(frame);setFailure(true);}})
          .finally(()=>{active--;pending.delete(frame);controllers.delete(controller);if(!disposed)raf=requestAnimationFrame(pump);});
      }
    };
    const state={p:0};
    let ctx;
    Promise.all([import('gsap'), import('gsap/ScrollTrigger')]).then(([gsapModule, triggerModule]) => {
      if(disposed) return;
      const gsap=gsapModule.gsap;
      const ScrollTrigger=triggerModule.ScrollTrigger;
      gsap.registerPlugin(ScrollTrigger);
      ctx=gsap.context(()=>{
      const animation=gsap.to(state,{p:1,ease:'none',onUpdate:()=>{
        if(disposed) return;
        currentProgress=state.p;setCaption(state.p);
        if(manifest){target=Math.min(manifest.count-1,Math.round(Math.min(1,state.p/.62)*(manifest.count-1)));draw();pump();}
      },scrollTrigger:{trigger:stage,start:'top top',end:()=>`+=${Math.max(6200,innerHeight*8)}`,pin:true,scrub:.45,invalidateOnRefresh:true,anticipatePin:1}});
      trigger.current=animation.scrollTrigger;
      },root);
      ScrollTrigger.refresh();
      const contentHashes=['#residence','#featured-residence','#architecture','#location','#gallery','#journal','#office'];
      if(contentHashes.includes(entryHash.current) && location.hash===entryHash.current){
        const destination=document.getElementById(entryHash.current.slice(1));
        requestAnimationFrame(()=>{if(!disposed)destination?.scrollIntoView({behavior:'instant',block:'start'});});
      }
      entryHash.current=null;
    }).catch(()=>{if(!disposed)setFailure(true);});
    // The first image is available immediately; the sequence starts loading after first paint.
    const load=()=>fetch('/media/journey.json').then(r=>{if(!r.ok)throw Error();return r.json();}).then(data=>{
      if(disposed)return; manifest=data;target=Math.min(data.count-1,Math.round(Math.min(1,currentProgress/.62)*(data.count-1)));pump();
    }).catch(()=>{if(!disposed)setFailure(true);});
    const timer=setTimeout(load,700);
    const resize=()=>{lastDrawn=-1;draw();}; window.addEventListener('resize',resize);
    return ()=> {disposed=true;clearTimeout(timer);cancelAnimationFrame(raf);controllers.forEach(c=>c.abort());cache.forEach(b=>b.close());ctx?.revert();trigger.current=null;window.removeEventListener('resize',resize);};
  }, [cinematic]);

  const jump = p => {const st=trigger.current;if(st)window.scrollTo({top:st.start+(st.end-st.start)*p,behavior:'instant'});};
  const goStill = i => {const next=Math.max(0,Math.min(stills.length-1,i));setIndex(next);scrollGallery.current?.children[next]?.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth',block:'nearest',inline:'start'});};
  const switchToStills = () => {setCinematic(false);requestAnimationFrame(()=>{document.getElementById('still-tour')?.scrollIntoView({behavior:'instant'});scrollGallery.current?.focus({preventScroll:true});});};

  return <>
    <section ref={root} className={`journey ${cinematic?'is-cinematic':'is-still'}`} aria-label="Explore Aurelia House">
      <picture className="hero-image"><source media="(max-width: 640px)" srcSet="/media/exterior-portrait.webp"/><img src="/media/exterior.webp" width="1920" height="1086" alt="A low travertine residence with an open oak doorway, olive trees and a reflecting pool" fetchPriority="high" /></picture>
      {cinematic && <>
        <canvas ref={canvas} className="journey-canvas" role="img" aria-label="Scroll-controlled camera journey from the exterior through the open entrance into the living room" />
        {['kitchen','detail','terrace'].map(name=><img className={`cinematic-still cinematic-${name}`} key={name} src={`/media/${name}.webp`} width="1920" height="1080" alt="" loading="lazy" decoding="async" />)}
      </>}
      <div className="hero-shade" />
      <div ref={hero} className="hero-copy">
        <p className="hero-kicker">ARCHITECTURE. LIGHT. LANDSCAPE.</p>
        <h1>A quieter<br/>kind of <em>rare.</em></h1>
        <p className="hero-description">Exceptional residences shaped by<br/>the things that matter.</p>
        <a className="explore-link" href={cinematic?'#journey-control':'#still-tour'} onClick={cinematic?e=>{e.preventDefault();jump(.12);progressInput.current?.focus({preventScroll:true});}:undefined}>Explore the residence <span aria-hidden="true">↗</span></a>
      </div>
      <div className="scene-caption" aria-hidden="true"><p ref={label}>{chapters[0].label}</p><h2 ref={title}>{chapters[0].title}</h2></div>
      <div className="journey-bottom">
        <span className="property-caption">AURELIA HOUSE <span>AN ARCHITECTURAL CONCEPT</span></span>
        {cinematic ? <div className="journey-tools">
          <button onClick={switchToStills}>View as stills</button>
          <label className="sr-only" htmlFor="journey-control">Position in the residence tour</label>
          <input id="journey-control" ref={progressInput} type="range" min="0" max="1000" defaultValue="0" onChange={e=>jump(Number(e.target.value)/1000)} aria-valuetext="The approach" />
          <a href="#residence">Skip tour <span aria-hidden="true">↓</span></a>
        </div>:<a className="still-scroll-link" href="#still-tour">Discover the spaces <span aria-hidden="true">↓</span></a>}
      </div>
      {cinematic && <div className="journey-progress" aria-hidden="true"><span ref={progressBar}/></div>}
      {cinematic && failure && <button className="sequence-notice" onClick={switchToStills}>Tour unavailable. Explore the stills ↗</button>}
      {cinematic && !ready && !failure && <span className="sequence-loading" role="status">Preparing your visit</span>}
    </section>
    {!cinematic && <section id="still-tour" className="still-tour" aria-label="Residence tour in photographs">
      <div className="still-tour-heading"><div><p className="eyebrow">A WALK THROUGH THE HOUSE</p><h2>A sense of arrival.</h2></div><div className="still-controls"><button onClick={()=>goStill(index-1)} disabled={index===0} aria-label="Previous space">←</button><span>{String(index+1).padStart(2,'0')} / 07</span><button onClick={()=>goStill(index+1)} disabled={index===6} aria-label="Next space">→</button></div></div>
      <div ref={scrollGallery} className="still-gallery" tabIndex="0" aria-label="Photographic tour. Use arrow controls or scroll horizontally to explore the spaces." onScroll={e=>{const el=e.currentTarget;setIndex(Math.round(el.scrollLeft/(el.firstElementChild?.getBoundingClientRect().width+20||1)));}}>
        {stills.map(([name,heading,text],i)=><figure key={name}><picture><source media="(max-width:640px)" srcSet={`/media/${name}-small.webp`}/><img src={`/media/${name}.webp`} width="1600" height="900" loading="lazy" alt={text}/></picture><figcaption><span>{String(i+1).padStart(2,'0')}</span><div><h3>{heading}</h3><p>{text}</p></div></figcaption></figure>)}
      </div>
    </section>}
  </>;
}
